import moment from "moment-timezone";
import type { SalonService } from "../../../../types/salon";
import { dayOf, staffServiceFor } from "../booking/bookingTypes";
import type { StaffAvailability } from "./staffAvailability";
import type { WaitlistToast } from "./waitlistModel";

const pad = (n: number) => String(n).padStart(2, "0");

/** "now": start at the current minute; "after": the first gap that fits. */
export type AssignMode = "now" | "after";

/** A provider pick: a staff id, or "any" (random among the available). */
export type AssignChoice = string | "any";

const MINUTE_MS = 60 * 1000;

/** The exact start minute (seconds dropped; no rounding to 5 min). */
export const toMinute = (ms: number): number =>
  Math.floor(ms / MINUTE_MS) * MINUTE_MS;

/** A salon wall-clock fake-local Date (calendar events) as an instant. */
export const wallClockToInstant = (d: Date, timezone: string): number =>
  moment
    .tz(
      `${dayOf(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
      "YYYY-MM-DD HH:mm:ss",
      timezone,
    )
    .valueOf();

/**
 * The appointment start (ISO UTC) for a pick: "after" = the provider's
 * first gap that fits the service (`freeAt`), "now" (or nothing to wait
 * for) = the current minute.
 */
export const assignStartIso = (
  mode: AssignMode,
  who: Pick<StaffAvailability, "freeAt">,
  nowMs: number,
  timezone: string,
): string =>
  new Date(
    mode === "after" && who.freeAt
      ? toMinute(Math.max(wallClockToInstant(who.freeAt, timezone), nowMs))
      : toMinute(nowMs),
  ).toISOString();

/** "Any available": a random available provider who performs the service. */
export const pickAnyAvailable = (
  rows: readonly StaffAvailability[],
  random: () => number = Math.random,
): StaffAvailability | null => {
  const pool = rows.filter((r) => r.available);
  return pool.length ? pool[Math.floor(random() * pool.length)] : null;
};

/**
 * The client's requested provider (walk-in portal `preferred_staff_id`)
 * moved to the top; null when unset or not in rows (no longer performs the
 * service), and the rows stay as they were.
 */
export const requestedFirst = (
  rows: readonly StaffAvailability[],
  requestedId: string | null | undefined,
): { rows: StaffAvailability[]; requested: StaffAvailability | null } => {
  const requested = requestedId
    ? rows.find((r) => r.id === requestedId) || null
    : null;
  return requested
    ? { rows: [requested, ...rows.filter((r) => r !== requested)], requested }
    : { rows: [...rows], requested: null };
};

/** Resolve a choice to a provider (null = nobody available / unknown). */
export const resolveChoice = (
  choice: AssignChoice,
  rows: readonly StaffAvailability[],
  random?: () => number,
): StaffAvailability | null =>
  choice === "any"
    ? pickAnyAvailable(rows, random)
    : rows.find((r) => r.id === choice) || null;

/** The booked_services staff_service_id for (service, provider). */
export const staffServiceIdFor = (
  service: SalonService | undefined,
  staffId: string,
): string | undefined => staffServiceFor(service, staffId)?.id;

export { serviceMinutesFor } from "./staffAvailability";

/** Prototype success toast: "Assigned walk-in (Maria Lopez) to Sarah Kim". */
export const assignedToast = ({
  client,
  staff,
  service,
  startIso,
  minutes,
  timezone,
  after,
}: {
  client: string;
  staff: string;
  service: string;
  startIso: string;
  minutes: number;
  timezone: string;
  /** Queued behind bookings: the busy step's "After …" label. */
  after?: string;
}): WaitlistToast => {
  const start = moment.utc(startIso).tz(timezone);
  const end = start.clone().add(minutes, "minutes");
  return {
    title: `Assigned walk-in (${client}) to ${staff}`,
    note: `${service} · ${start.format("h:mm A")} – ${end.format("h:mm A")}${after ? ` · ${after.toLowerCase()}` : ""}`,
  };
};
